import { Routes } from '@common/constants';
import { useAppContext } from '@common/contexts/AppContext';
import { useLabels } from '@features/Menu/useLabels';
import { useModuleStatuses, usePropertyModules } from '@features/properties';
import { useRoomPageSettings } from '@features/rooms';
import {
  useGetSpaClassesPage,
  useGetSpaDaysPage,
  useGetSpaFacilitiesPage,
  useGetSpaTreatmentsPage,
} from '@features/spa/hooks';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { Category } from 'shared-types/Common';
import { Product } from 'shared-types/Product';
import { CategoryItem, CollectionItem, FeaturedLinkItem, HomeTemplate } from 'voyager';
import { buildCardImageUrl } from '@utils/buildCardImageUrl';
import { useFeaturedModuleVisibility } from '../useFeaturedModuleVisibility';
import { HomeV2Props } from './HomeV2.types';

type HomepageFeaturedItem = {
  title: string;
  subtitle?: string;
  url?: string;
  slug?: string;
  htmlTarget?: string;
  image?: { url?: string };
};

type HomepageCategory = Category & { isPrivate?: boolean };

type HomepageEntity = {
  featuredItem?: HomepageFeaturedItem;
  categories?: HomepageCategory[];
  heroBackgroundImage?: { url?: string };
};

export const HomeV2: FC<HomeV2Props> = ({ property, language }) => {
  const router = useRouter();
  const { t } = useTranslations();
  const labels = useLabels();
  const { siteId } = useAppContext();

  const { data: modules } = usePropertyModules(siteId);
  const { isFeaturedSpaDaysVisible, isFeaturedSpaTreatmentsVisible } =
    useFeaturedModuleVisibility();

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();

  const homepageId = property.homepage?.id;
  const homepage = useSelector((state) =>
    homepageId ? (entitySelectors.getHomepage(state, homepageId) as HomepageEntity) : null
  );

  const isSpaEnabled = modules?.spa?.enabled ?? false;
  const isClassesVisible = isSpaEnabled && (modules?.spa?.features?.classes?.enabled ?? false);
  const isFacilitiesVisible =
    isSpaEnabled && (modules?.spa?.features?.facilities?.enabled ?? false);
  const isRetailVisible = property.isRetailEnabled;

  const { content: roomsPage } = useRoomPageSettings(language, property.name);
  const { data: spaDaysPage } = useGetSpaDaysPage(property.id, language);
  const { data: spaTreatmentsPage } = useGetSpaTreatmentsPage(property.id, language);
  const { data: spaClassesPage } = useGetSpaClassesPage(property.id, language);
  const { data: spaFacilitiesPage } = useGetSpaFacilitiesPage(property.id, language);
  const { data: tablesPage } = useGetTablesPage(property.id, language);

  const products = useSelector(entitySelectors.getAllProducts) as Product[];
  const firstProductImageUrl = products?.[0]?.imageGallery?.length
    ? products[0].imageGallery[0]?.url
    : products?.[0]?.image?.url;
  const productsTileImageUrl = buildCardImageUrl(firstProductImageUrl);

  const heroImageUrl =
    homepage?.heroBackgroundImage?.url || property.homepage?.heroBackgroundImage?.url || null;
  const propertyLogoUrl = property.logo?.url || null;

  const categories = useMemo((): CategoryItem[] => {
    const items: CategoryItem[] = [];

    if (isRoomsEnabled) {
      items.push({
        id: 'rooms',
        label: labels?.rooms || t('home.rooms'),
        imageUrl: roomsPage?.heroBackgroundImage,
      });
    }

    if (property.isTablesIntegrated) {
      items.push({
        id: 'tables',
        label: labels?.tables || t('app.dining'),
        imageUrl: tablesPage?.heroBackgroundImage?.url,
      });
    }

    if (isFeaturedSpaDaysVisible) {
      items.push({
        id: 'spa-days',
        label: labels?.spadays || t('home.spaDays'),
        imageUrl: spaDaysPage?.heroBackgroundImage?.url,
      });
    }

    if (isFeaturedSpaTreatmentsVisible) {
      items.push({
        id: 'spa-treatments',
        label: labels?.treatments || t('home.spaTreatments'),
        imageUrl: spaTreatmentsPage?.heroBackgroundImage?.url,
      });
    }

    if (isFacilitiesVisible) {
      items.push({
        id: 'facilities',
        label: labels?.facilities || t('app.facilities'),
        imageUrl: spaFacilitiesPage?.heroBackgroundImage?.url,
      });
    }

    if (isClassesVisible) {
      items.push({
        id: 'spa-classes',
        label: labels?.classes || t('common.classes'),
        imageUrl: spaClassesPage?.heroBackgroundImage?.url,
      });
    }

    if (isRetailVisible) {
      items.push({
        id: 'products',
        label: labels?.retail || t('category.shop'),
        imageUrl: productsTileImageUrl,
      });
    }

    return items;
  }, [
    isRoomsEnabled,
    isFeaturedSpaDaysVisible,
    isFeaturedSpaTreatmentsVisible,
    isClassesVisible,
    isFacilitiesVisible,
    isRetailVisible,
    productsTileImageUrl,
    property.isTablesIntegrated,
    labels,
    t,
    roomsPage,
    spaDaysPage,
    spaTreatmentsPage,
    spaClassesPage,
    spaFacilitiesPage,
    tablesPage,
  ]);

  const handleSelectCategory = useCallback(
    (id: string) => {
      const routeMap: Record<string, string> = {
        rooms: Routes.ROOM_LIST,
        'spa-days': Routes.SPA_DAY_LIST,
        'spa-treatments': Routes.SPA_TREATMENT_LIST,
        'spa-classes': Routes.SPA_CLASSES_LIST,
        facilities: Routes.FACILITY_LIST,
        tables: Routes.TABLES_HOME,
        products: Routes.PRODUCT_LIST,
      };

      const route = routeMap[id];
      if (route) {
        router.push({ pathname: route });
      }
    },
    [router]
  );

  const featuredLink = useMemo<FeaturedLinkItem | null>(() => {
    const item = homepage?.featuredItem;
    if (!item) return null;

    const ctaLabel = item.slug === 'tables' ? t('home.bookTable') : t('home.viewDetails');

    const onCtaClick = () => {
      if (item.slug === 'tables') {
        router.push({ pathname: Routes.TABLES_HOME });
        return;
      }
      if (!item.url) return;
      if (!/^(https?:\/\/|\/)/.test(item.url)) return;
      const target = item.htmlTarget === '_blank' ? '_blank' : '_self';
      window.open(item.url, target, 'noopener,noreferrer');
    };

    return {
      title: item.title,
      subtitle: item.subtitle,
      imageUrl: item.image?.url || null,
      ctaLabel,
      onCtaClick,
    };
  }, [homepage?.featuredItem, router, t]);

  const collections = useMemo<CollectionItem[]>(() => {
    const list = homepage?.categories;
    if (!list?.length) return [];
    return list
      .filter((category) => !category.isPrivate)
      .map((category) => ({
        id: String(category.id),
        label: category.name,
        imageUrl: category.image?.url || null,
      }));
  }, [homepage?.categories]);

  const handleSelectCollection = useCallback(
    (id: string) => {
      router.push({ pathname: Routes.CATEGORY_DETAIL, query: { id } });
    },
    [router]
  );

  return (
    <HomeTemplate
      propertyName={property.name}
      propertyLogoUrl={propertyLogoUrl}
      heroImageUrl={heroImageUrl}
      categories={categories}
      onSelectCategory={handleSelectCategory}
      featuredLink={featuredLink}
      collections={collections}
      onSelectCollection={handleSelectCollection}
      collectionsHeading={t('homepage.featuredCollections')}
    />
  );
};
